import { useState, useEffect } from 'react';
import useSWR from 'swr';

import { IRepository } from '@/types';
import { IResponseError } from '@/lib/apiBase';
import { fetchSchema, IRepositorySchemaResponse, ISchemaSummary } from '@/lib/repositorySchemasApi';

interface useSchemaParams {
  repository: IRepository;
  schema_hash: string;
  initialData?: IRepositorySchemaResponse | undefined;
}

const useSchema = ({ repository, schema_hash, initialData }: useSchemaParams) => {
  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [schema, setSchema] = useState<ISchemaSummary | undefined>(initialData?.schema || undefined);
  const [error, setError] = useState<IResponseError | undefined>();

  const { data, isLoading, isValidating } = useSWR(
    [repository.namespace, repository.name, schema_hash],
    () =>
      fetchSchema({
        repositoryName: repository.name,
        namespace: repository.namespace,
        schema_hash: schema_hash,
      }),
    {
      fallbackData: initialData,
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
    },
  );

  useEffect(() => {
    if (data) {
      setSchema(data?.schema);
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  return {
    status: status,
    isLoading: isLoading,
    isValidating: isValidating,
    schema: schema,
    error: error,
  };
};

export default useSchema;
